import { useEffect, useRef } from "react";
import { createChart, LineSeries, HistogramSeries } from "lightweight-charts";
import { Format } from "@/utils/formatters";
import { PositionsBotHistory } from "@/api/positions/types";
import { ChartPnlType } from "./portfolio-chart";

const PortfolioTWChart = ({
  data,
  activePnl,
}: {
  data: PositionsBotHistory[];
  activePnl: ChartPnlType;
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReturnType<typeof createChart>>();

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: {
          color: "transparent",
        },
        textColor: "#ffffff",
      },
      grid: {
        horzLines: { color: "#121521" },
        vertLines: { color: "#121521" },
      },
      crosshair: {
        horzLine: { labelBackgroundColor: "#ffffff" },
        vertLine: { labelBackgroundColor: "#ffffff" },
      },
      rightPriceScale: {
        visible: true,
        minimumWidth: 80,
      },
      timeScale: {
        timeVisible: true,
        tickMarkFormatter: (time: string) => {
          return time as string;
        },
      },
      localization: {
        dateFormat: "dd-MM-yyyy",
        timeFormatter: (time: string) => {
          return time as string;
        },
      },
      width: chartContainerRef.current.clientWidth,
    });

    chart.timeScale().fitContent();
    chartRef.current = chart;

    if (activePnl === "Cumulative PNL") {
      const lineSeries = chart.addSeries(LineSeries, {
        color: "#ffffff",
        lineWidth: 2,
        priceLineVisible: false,
        priceFormat: {
          type: "custom",
          formatter: (value: number) =>
            `${value < 0 ? "-" : ""}${Format.from(value)
              .unit(value !== 0)
              .currency()}`,
        },
      });

      lineSeries.setData(
        data.map(({ date, cumulativePnl }) => ({
          time: date,
          value: cumulativePnl,
        }))
      );
    } else {
      const histogramSeries = chart.addSeries(HistogramSeries, {
        priceLineVisible: false,
        priceFormat: {
          type: "custom",
          formatter: (value: number) =>
            `${value < 0 ? "-" : ""}${Format.from(value)
              .unit(value !== 0)
              .currency()}`,
        },
      });

      histogramSeries.setData(
        data.map(({ date, dailyPnl }) => ({
          time: date,
          value: dailyPnl,
          color: dailyPnl >= 0 ? "#5ff0e0" : "#717af7",
        }))
      );
    }

    const resizeObserver = new ResizeObserver(() => {
      chart.applyOptions({
        width: chartContainerRef.current!.clientWidth,
      });
    });

    resizeObserver.observe(chartContainerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.remove();
    };
  }, [data, activePnl]);

  return (
    <div className="flex flex-1 items-center justify-center pl-[0.625rem]">
      <div ref={chartContainerRef} className="w-full h-full" />
    </div>
  );
};

export default PortfolioTWChart;
